'use client';

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { IntlContext } from 'react-intl';
import intl, { type IntlConfig } from './intl';
import { enterWithIntl } from './intl';
import { insertIntl, loadIntl } from './intl/load';
import { provideFallback } from './intl/not-found';
import { type IntlShape } from './intl/types';
import { type LocaleId } from './locales';

export function PrecacheLocale(props: {
  locale: LocaleId;
  messages: IntlConfig['messages'];
}) {
  insertIntl(props.locale, props.messages);
  return null;
}

export function usePrecacheLocale(config?: IntlConfig) {
  if (!config) {
    return undefined;
  }
  return insertIntl(config.locale as LocaleId, config.messages);
}

export function useSetLocale(current?: IntlShape) {
  if (current) {
    enterWithIntl(current);
  }
  useSyncExternalStore(
    useCallback(
      () => (current ? provideFallback(current, 'SetLocale') : () => undefined),
      [current]
    ),
    NOP,
    NOP
  );
  return null;
}

export function LocalizeClient(props: {
  config?: IntlConfig;
  children: React.ReactNode;
}) {
  const ctx = usePrecacheLocale(props.config);
  useSetLocale(ctx);

  return <IntlContext value={ctx ?? intl()}>{props.children}</IntlContext>;
}

export function RefreshOnLanguageChange(props: { onRefresh: () => void }) {
  const refresh = props.onRefresh ?? hardReload;
  useEffect(() => {
    addEventListener('languagechange', refresh);
    return () => {
      removeEventListener('languagechange', refresh);
    };
  }, [refresh]);

  return null;
}

function hardReload() {
  window.location.reload();
}

export function LocaleProvider(props: {
  children: React.ReactNode;
  locale: string;
}) {
  const value = loadIntl(props.locale)?.unwrap();
  useSetLocale(value);
  return <IntlContext value={value ?? intl()}>{props.children}</IntlContext>;
}

function NOP() {
  return null;
}
